import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-hot-toast';
import { 
  Upload as UploadIcon, 
  File, 
  X, 
  Eye, 
  EyeOff, 
  Copy, 
  CheckCircle, 
  AlertCircle,
  Clock,
  Shield,
  Download
} from 'lucide-react';

import { uploadAPI, handleAPIError } from '../utils/api';
import { formatFileSize, getFileIcon, getFileCategory, copyToClipboard, generatePassword, getShareUrl } from '../utils/format';

export function Upload() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadLimits, setUploadLimits] = useState(null);
  
  // Form state
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [expirationHours, setExpirationHours] = useState(24);
  const [maxDownloads, setMaxDownloads] = useState('');
  
  // Load upload limits on component mount
  useEffect(() => {
    const loadLimits = async () => {
      try {
        const response = await uploadAPI.getUploadLimits();
        setUploadLimits(response.data);
      } catch (error) {
        console.error('Failed to load upload limits:', error);
      }
    };
    
    loadLimits();
  }, []);
  
  // File validation
  const validateFile = useCallback((file) => {
    if (!uploadLimits) return true;
    
    // Check file size
    if (file.size > uploadLimits.maxFileSize) {
      toast.error(`File size exceeds ${formatFileSize(uploadLimits.maxFileSize)} limit`);
      return false;
    }
    
    // Check MIME type
    if (!uploadLimits.allowedMimeTypes.includes(file.type)) {
      toast.error(`File type "${file.type}" is not allowed`);
      return false;
    }
    
    return true;
  }, [uploadLimits]);
  
  // Dropzone configuration
  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.tiff'],
      'video/*': ['.mp4', '.webm', '.ogg', '.avi', '.mov', '.wmv', '.flv', '.mkv'],
      'audio/*': ['.mp3', '.wav', '.ogg', '.flac', '.aac', '.wma', '.m4a'],
      'text/*': ['.txt', '.md', '.csv', '.json', '.xml', '.html', '.css', '.js', '.ts'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-powerpoint': ['.ppt'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
      'application/zip': ['.zip'],
      'application/x-rar-compressed': ['.rar'],
      'application/x-7z-compressed': ['.7z'],
      'application/x-tar': ['.tar'],
      'application/gzip': ['.gz'],
      'application/octet-stream': ['.bin', '.exe', '.dmg', '.iso', '.deb', '.rpm']
    },
    maxFiles: 1,
    onDrop: useCallback((acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        toast.error('Please select only one file');
        return;
      }
      
      const file = acceptedFiles[0];
      if (file && validateFile(file)) {
        setSelectedFile(file);
        setUploadResult(null);
      }
    }, [validateFile])
  });
  
  // Upload file
  const handleUpload = async () => {
    if (!selectedFile) return;
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      // Step 1: Request upload URL
      const uploadRequest = await uploadAPI.requestUpload({
        filename: selectedFile.name,
        size: selectedFile.size,
        contentType: selectedFile.type,
        password: password.trim() || undefined,
        expirationHours,
        maxDownloads: maxDownloads ? parseInt(maxDownloads) : undefined
      });
      
      // Step 2: Upload file to S3
      await uploadAPI.uploadToS3(
        uploadRequest.data.uploadUrl,
        selectedFile,
        setUploadProgress
      );
      
      // Step 3: Confirm upload
      const confirmResult = await uploadAPI.confirmUpload({
        fileKey: uploadRequest.data.fileKey,
        filename: selectedFile.name,
        originalName: selectedFile.name,
        size: selectedFile.size,
        contentType: selectedFile.type,
        password: password.trim() || undefined,
        expirationHours,
        maxDownloads: maxDownloads ? parseInt(maxDownloads) : undefined
      });
      
      setUploadResult(confirmResult.data);
      toast.success('File uploaded successfully!');
      
    } catch (error) {
      const errorMessage = handleAPIError(error);
      toast.error(errorMessage);
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };
  
  // Reset form
  const handleReset = () => {
    setSelectedFile(null);
    setUploadResult(null);
    setPassword('');
    setExpirationHours(24);
    setMaxDownloads('');
    setUploadProgress(0);
  };
  
  // Copy share URL
  const handleCopyUrl = async () => {
    if (uploadResult?.shareUrl) {
      try {
        await copyToClipboard(uploadResult.shareUrl);
        toast.success('Share URL copied to clipboard!');
      } catch (error) {
        toast.error('Failed to copy URL');
      }
    }
  };
  
  // Generate random password
  const handleGeneratePassword = () => {
    const newPassword = generatePassword();
    setPassword(newPassword);
    toast.success('Password generated!');
  };
  
  // Get dropzone class names
  const getDropzoneClasses = () => {
    let classes = 'dropzone w-full border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-300 ';
    
    if (isDragAccept) {
      classes += 'border-success-500 bg-success-50 ';
    } else if (isDragReject) {
      classes += 'border-danger-500 bg-danger-50 ';
    } else if (isDragActive) {
      classes += 'border-primary-500 bg-primary-50 ';
    } else {
      classes += 'border-gray-300 bg-gray-50 hover:border-primary-400 hover:bg-primary-50 ';
    }
    
    return classes;
  };
  
  if (uploadResult) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-success-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Upload Successful!</h1>
            <p className="text-gray-600">Your file has been uploaded and is ready to share.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="text-2xl">{getFileIcon(uploadResult.mimeType)}</div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 truncate">{uploadResult.originalName}</h3>
                  <p className="text-sm text-gray-500">
                    {formatFileSize(uploadResult.size)} • {getFileCategory(uploadResult.mimeType)}
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">Expires: {new Date(uploadResult.expiresAt).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Download className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">Downloads: {uploadResult.downloadCount}</span>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Share URL</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={uploadResult.shareUrl}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono"
                  />
                  <button
                    onClick={handleCopyUrl}
                    className="btn btn-primary"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Shield className="h-4 w-4" />
                <span>{password ? 'Password protected' : 'No password protection'}</span>
              </div>
            </div>
          </div>
          
          <div className="flex justify-center space-x-4">
            <button
              onClick={handleReset}
              className="btn btn-secondary"
            >
              Upload Another File
            </button>
            <a
              href={uploadResult.shareUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
            >
              View Share Page
            </a>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="text-center mb-8">
        <div className="mx-auto w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
          <UploadIcon className="h-8 w-8 text-primary-600" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Upload & Share Files</h1>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Securely upload files and share them with automatic expiration. Files are stored privately and can be password protected.
        </p>
      </div>
      
      <div className="bg-white rounded-xl shadow-lg p-8">
        {/* File Upload Area */}
        <div className="mb-8">
          <div {...getRootProps()} className={getDropzoneClasses()}>
            <input {...getInputProps()} />
            <div className="space-y-4">
              <div className="mx-auto w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                <UploadIcon className="h-8 w-8 text-gray-400" />
              </div>
              
              {selectedFile ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="text-2xl">{getFileIcon(selectedFile.type)}</div>
                    <div className="text-left">
                      <p className="font-semibold text-gray-900">{selectedFile.name}</p>
                      <p className="text-sm text-gray-500">
                        {formatFileSize(selectedFile.size)} • {getFileCategory(selectedFile.type)}
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFile(null);
                      }}
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {isDragActive ? 'Drop your file here' : 'Drag & drop a file here'}
                  </h3>
                  <p className="text-gray-500">or click to select from your computer</p>
                  {uploadLimits && (
                    <p className="text-sm text-gray-400">
                      Max file size: {formatFileSize(uploadLimits.maxFileSize)}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Upload Options */}
        {selectedFile && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password Protection (Optional)
                </label>
                <div className="flex items-center space-x-2">
                  <div className="relative flex-1">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter password to protect file"
                      className="input pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={handleGeneratePassword}
                    className="btn btn-secondary whitespace-nowrap"
                  >
                    Generate
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expiration Time
                </label>
                <select
                  value={expirationHours}
                  onChange={(e) => setExpirationHours(parseInt(e.target.value))}
                  className="input"
                >
                  <option value={1}>1 Hour</option>
                  <option value={6}>6 Hours</option>
                  <option value={24}>24 Hours</option>
                  <option value={72}>3 Days</option>
                  <option value={168}>1 Week</option>
                </select>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Download Limit (Optional)
                </label>
                <input
                  type="number"
                  value={maxDownloads}
                  onChange={(e) => setMaxDownloads(e.target.value)}
                  placeholder="Unlimited"
                  min="1"
                  max="1000"
                  className="input"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Leave empty for unlimited downloads
                </p>
              </div>
              
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Upload Summary</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• File will expire in {expirationHours} hours</li>
                  <li>• {password ? 'Password protected' : 'No password protection'}</li>
                  <li>• {maxDownloads ? `Limited to ${maxDownloads} downloads` : 'Unlimited downloads'}</li>
                  <li>• File size: {formatFileSize(selectedFile.size)}</li>
                </ul>
              </div>
            </div>
          </div>
        )}
        
        {/* Upload Progress */}
        {uploading && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Uploading...</span>
              <span className="text-sm text-gray-500">{uploadProgress}%</span>
            </div>
            <div className="progress-bar">
              <div 
                className="progress-bar-fill"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="flex justify-center space-x-4">
          <button
            onClick={handleReset}
            disabled={uploading}
            className="btn btn-secondary"
          >
            Reset
          </button>
          <button
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            className="btn btn-primary"
          >
            {uploading ? 'Uploading...' : 'Upload File'}
          </button>
        </div>
      </div>
      
      {/* Upload Limits Info */}
      {uploadLimits && (
        <div className="mt-8 bg-blue-50 rounded-lg p-6">
          <h3 className="font-semibold text-blue-900 mb-3">Upload Limits & Info</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-blue-700 font-medium">File Size Limit</p>
              <p className="text-blue-600">{formatFileSize(uploadLimits.maxFileSize)}</p>
            </div>
            <div>
              <p className="text-blue-700 font-medium">Hourly Upload Limit</p>
              <p className="text-blue-600">{uploadLimits.maxFilesPerHour} files per hour</p>
            </div>
            <div>
              <p className="text-blue-700 font-medium">Maximum Expiration</p>
              <p className="text-blue-600">{uploadLimits.maxExpirationHours} hours (7 days)</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 